import {
  Dialog,
  Button,
  Flex,
  Text,
  Select,
  TextArea,
  IconButton,
  Icon,
} from '@scale/scaleui-radix';
import _ from 'lodash';
import { useState, useMemo } from 'react';
import {
  useCreateSuspiciousBehaviorAudit,
  CreateSuspiciousBehaviorAuditResponse,
  CreateSuspiciousBehaviorAuditRequest,
} from 'react-scale/components/fraud/hooks/useCreateSuspiciousBehaviorAudit';
import FileUpload from 'react-scale/corp/components/UI/FileUpload/FileUpload';
import { toast } from 'react-toastify';
import {
  AuditType,
  SuspiciousBehaviorAuditType,
  SourceDetails,
  FraudCategory,
} from '@scale/shared/trustAndSafety/tnsAudits/types';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

interface ReportSuspiciousContributorDialogProps {
  contributorId: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSuccess?: () => void;
  presetSuspiciousTaskAttemptIds?: string[];
  showFraudCategories?: boolean;
  sourceDetails?: SourceDetails;
}

enum ViolationType {
  Cheating = 'cheating',
  Spam = 'spam',
  CredentialFraud = 'credential_fraud',
  IdentityFraud = 'identity_fraud',
  LocationFraud = 'location_fraud',
}

const getAuditParams = (
  violationType: ViolationType,
): { auditType: SuspiciousBehaviorAuditType; fraudCategory?: FraudCategory } => {
  switch (violationType) {
    case ViolationType.Cheating:
      return { auditType: AuditType.TaskingCheating };
    case ViolationType.Spam:
      return { auditType: AuditType.TaskingSpam };
    case ViolationType.CredentialFraud:
      return {
        auditType: AuditType.ManuallyReportedFraud,
        fraudCategory: FraudCategory.CredentialResumeFraud,
      };
    case ViolationType.IdentityFraud:
      return {
        auditType: AuditType.ManuallyReportedFraud,
        fraudCategory: FraudCategory.IdentityFraud,
      };
    case ViolationType.LocationFraud:
      return {
        auditType: AuditType.ManuallyReportedFraud,
        fraudCategory: FraudCategory.LocationFraud,
      };
    default:
      return { auditType: AuditType.TaskingCheating };
  }
};

export default function ReportSuspiciousContributorDialog({
  contributorId,
  isOpen,
  onOpenChange,
  onSuccess,
  presetSuspiciousTaskAttemptIds,
  showFraudCategories = true,
  sourceDetails,
}: ReportSuspiciousContributorDialogProps) {
  const [suspiciousTaskAttemptIds, setSuspiciousTaskAttemptIds] = useState<string | undefined>(
    presetSuspiciousTaskAttemptIds?.join('\n'),
  );
  const [selectedViolationType, setSelectedViolationType] = useState<ViolationType>(
    ViolationType.Cheating,
  );
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);

  const fraudReportingEnabled = useUnleashFeatureFlag('contributor-fraud-reporting', {}, false);

  const requiresTaskAttempts = useMemo(() => {
    return (
      selectedViolationType === ViolationType.Cheating ||
      selectedViolationType === ViolationType.Spam
    );
  }, [selectedViolationType]);

  const resetState = () => {
    setSelectedViolationType(ViolationType.Cheating);
    setSuspiciousTaskAttemptIds(presetSuspiciousTaskAttemptIds?.join('\n'));
    setReason(undefined);
    setFiles([]);
    setError(undefined);
  };

  const onMutateSuccess = (response: CreateSuspiciousBehaviorAuditResponse) => {
    resetState();
    onOpenChange(false);
    if (response.matchedAuditIds.length > 0) {
      toast.error(
        'Unable to submit report because a report already exists for this contributor and task attempt(s). Please refer to the existing report in the Ops Hub project page > Data Quality > Cheating & Spam.',
      );
    } else {
      toast.success(
        'Successfully reported suspicious contributor. The Trust and Safety team will audit the submitted information.',
      );
    }
    onSuccess?.();
  };

  const {
    mutate: createSuspiciousBehaviorAuditMutate,
    isLoading: isCreateSuspiciousBehaviorAuditLoading,
  } = useCreateSuspiciousBehaviorAudit();

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content size="1" className="scale-tailwind p-4">
        <Dialog.Title size="3">
          <Flex justify="between" align="center">
            <Text>
              {showFraudCategories && fraudReportingEnabled
                ? 'Flag as Suspicious'
                : 'Report Cheating or Spam'}
            </Text>
            <Dialog.Close>
              <IconButton size="1" variant="soft" color="neutral">
                <Icon label="close" icon={faXmark} />
              </IconButton>
            </Dialog.Close>
          </Flex>
        </Dialog.Title>
        <Flex direction="column" gap="3">
          <label htmlFor="violation-type">
            <Text as="div" mb="1">
              Type of Violation{' '}
              <Text size="1" className="text-error-10">
                *
              </Text>
            </Text>
            <Select.Root
              size="2"
              value={selectedViolationType}
              onValueChange={value => setSelectedViolationType(value as ViolationType)}
            >
              <Select.Trigger />
              <Select.Content>
                {showFraudCategories && fraudReportingEnabled && (
                  <>
                    <Select.Item value={ViolationType.CredentialFraud}>
                      Credential/Resume fraud
                    </Select.Item>
                    <Select.Item value={ViolationType.IdentityFraud}>Identity fraud</Select.Item>
                    <Select.Item value={ViolationType.LocationFraud}>Location fraud</Select.Item>
                  </>
                )}
                <Select.Item value={ViolationType.Cheating}>Cheating</Select.Item>
                <Select.Item value={ViolationType.Spam}>Spam</Select.Item>
              </Select.Content>
            </Select.Root>
          </label>
          {!presetSuspiciousTaskAttemptIds && requiresTaskAttempts && (
            <label htmlFor="task-ids">
              <Text as="div" mb="1">
                Suspicious Task Attempt IDs{' '}
                <Text size="1" className="text-error-10">
                  *
                </Text>
              </Text>
              <TextArea
                id="task-attempt-ids"
                placeholder={'61f99c92c39a55002547eaf3\n61f99c92c39a55002547eaf4'}
                value={suspiciousTaskAttemptIds}
                onChange={e => setSuspiciousTaskAttemptIds(e.target.value)}
                resize="vertical"
              />
              <Text as="div" mb="1" className="scale-tailwind text-neutral-9" size="1">
                Task attempt IDs separated by comma or newline
              </Text>
            </label>
          )}
          <label htmlFor="rationale">
            <Text as="div" mb="1">
              Rationale (Fullstory links, queries, etc.){' '}
              <Text size="1" className="text-error-10">
                *
              </Text>
            </Text>
            <TextArea
              id="rationale"
              placeholder={`Why is this ${
                presetSuspiciousTaskAttemptIds ? 'task attempt' : 'contributor'
              } suspicious?`}
              value={reason}
              onChange={e => setReason(e.target.value)}
              resize="vertical"
            />
          </label>
          <label htmlFor="attachments">
            <Text as="div" mb="1">
              Attachments
            </Text>
            <FileUpload files={files} setFiles={setFiles} />
          </label>
        </Flex>

        <Flex mt="3" justify="between" align="center">
          <Text className="text-error-10">{error}</Text>
          <Flex gap="2">
            <Button color="neutral" variant="soft" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              disabled={(requiresTaskAttempts && !suspiciousTaskAttemptIds) || !reason}
              loading={isCreateSuspiciousBehaviorAuditLoading}
              onClick={() => {
                setError(undefined);
                const { auditType, fraudCategory } = getAuditParams(selectedViolationType);

                const request = {
                  contributorId,
                  type: auditType,
                  reason: reason!,
                  files,
                  sourceDetails,
                  ...(fraudCategory && { fraudCategory }),
                  ...(requiresTaskAttempts &&
                    suspiciousTaskAttemptIds && {
                      taskAttemptIds: _.uniq(
                        suspiciousTaskAttemptIds
                          .split(/[,\n]/)
                          .map(value => value.trim())
                          .filter(Boolean),
                      ),
                    }),
                };

                createSuspiciousBehaviorAuditMutate(
                  request as CreateSuspiciousBehaviorAuditRequest,
                  {
                    onSuccess: onMutateSuccess,
                    onError: (error: any) => {
                      setError(error.response?.message);
                    },
                  },
                );
              }}
            >
              Save
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
